import { Component } from '@theme/component';

class CrDeliverySlots extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.initDeliverySlots();
  }

  updatedCallback() {
    this.initDeliverySlots();
  }

  initDeliverySlots() {
    const container = this;
    const dateSelect = this.querySelector('#delivery-date-select');
    const timeSelect = this.querySelector('#delivery-time-select');
    const errorMsg = this.querySelector('#delivery-slot-error');
    
    // In this theme, the checkout button might be outside this component (e.g. in cart__ctas)
    const checkoutBtn = document.getElementById('checkout');
    const ctasContainer = document.querySelector('.cart__ctas');

    if (!dateSelect || !timeSelect) {
      if (!this._retryCount) this._retryCount = 0;
      if (this._retryCount < 10) {
        this._retryCount++;
        setTimeout(() => this.initDeliverySlots(), 50);
      }
      return;
    }
    
    // Reset retry count once initialized successfully
    this._retryCount = 0;

    const isRequired = container.getAttribute('data-required') === 'true';
    const daysLimit = parseInt(container.getAttribute('data-days-limit') || '7', 10);
    const savedDate = container.getAttribute('data-saved-date') || '';
    const savedTime = container.getAttribute('data-saved-time') || '';
    const hasDiwali = container.getAttribute('data-has-diwali') === 'true';
    const diwaliMinDateStr = container.getAttribute('data-diwali-min-date') || '2026-10-21';
    const FIXED_SLOT = '2:00 PM - 7:00 PM';

    // Block accelerated checkouts if required slot is not selected
    if (isRequired) {
      this.updateBlockerState(dateSelect, timeSelect, ctasContainer);
    }

    const now = new Date();
    
    // Standard min selectable date is 24 hours from now
    const standardMinDate = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    const standardMinDateStr = `${standardMinDate.getFullYear()}-${String(standardMinDate.getMonth() + 1).padStart(2, '0')}-${String(standardMinDate.getDate()).padStart(2, '0')}`;
    
    // If cart contains Diwali gift items, block all dates through October 20th
    let minDate = standardMinDate;
    let minDateStr = standardMinDateStr;
    if (hasDiwali) {
      if (diwaliMinDateStr > standardMinDateStr) {
        minDateStr = diwaliMinDateStr;
        minDate = new Date(diwaliMinDateStr + 'T00:00:00');
      }
    }

    // Max selectable date: extend through Diwali festive season (at least 25 days from min date) if Diwali items present
    let maxDate;
    if (hasDiwali) {
      const extendedDays = Math.max(daysLimit - 1, 25);
      maxDate = new Date(minDate.getTime() + extendedDays * 24 * 60 * 60 * 1000);
    } else {
      maxDate = new Date(minDate.getTime() + (daysLimit - 1) * 24 * 60 * 60 * 1000);
    }
    const maxDateStr = `${maxDate.getFullYear()}-${String(maxDate.getMonth() + 1).padStart(2, '0')}-${String(maxDate.getDate()).padStart(2, '0')}`;

    dateSelect.setAttribute('min', minDateStr);
    dateSelect.setAttribute('max', maxDateStr);

    // Initial Date selection: use saved date if valid and within bounds; otherwise default to allowed min date
    let initialDate = '';
    if (savedDate && savedDate >= minDateStr && savedDate <= maxDateStr) {
      initialDate = savedDate;
    } else {
      initialDate = minDateStr;
    }
    dateSelect.value = initialDate;

    // Filter and Populate Time Dropdown
    const updateTimeSlots = () => {
      // Clear options safely for all browsers (especially Safari)
      timeSelect.options.length = 0;
      timeSelect.add(new Option('Select a time...', ''));

      const selectedDateVal = dateSelect.value;

      if (!selectedDateVal) {
        timeSelect.disabled = true;
        if (isRequired) this.updateBlockerState(dateSelect, timeSelect, ctasContainer);
        return;
      }

      timeSelect.disabled = false;
      timeSelect.add(new Option(FIXED_SLOT, FIXED_SLOT, false, true));
      
      this.saveAttributesToCart(dateSelect, timeSelect, errorMsg, ctasContainer, isRequired);
    };

    // Listeners - only bind if not already bound
    if (!dateSelect.dataset.listenerBound) {
      dateSelect.addEventListener('click', function(e) {
        if (typeof this.showPicker === 'function') {
          try {
            this.showPicker();
          } catch (err) {}
        }
      });
      
      dateSelect.addEventListener('change', () => {
        // Enforce min date restriction in case of manual input
        if (dateSelect.value && dateSelect.value < minDateStr) {
          dateSelect.value = minDateStr;
          if (errorMsg) {
            const errText = errorMsg.querySelector('.cr-delivery-slots__error-text');
            if (errText) {
              errText.textContent = hasDiwali
                ? 'Diwali gifts are only available for delivery post 20th October (starting 21st Oct).'
                : 'Selected delivery date is not available.';
            }
            errorMsg.classList.remove('hidden');
          }
        }
        updateTimeSlots();
      });
      dateSelect.dataset.listenerBound = 'true';
    }

    if (!timeSelect.dataset.listenerBound) {
      timeSelect.addEventListener('change', () => {
        this.saveAttributesToCart(dateSelect, timeSelect, errorMsg, ctasContainer, isRequired);
      });
      timeSelect.dataset.listenerBound = 'true';
    }

    if (checkoutBtn && !checkoutBtn.dataset.deliverySlotListenerBound) {
      checkoutBtn.addEventListener('click', (e) => {
        if (isRequired && (!dateSelect.value || !timeSelect.value)) {
          e.preventDefault();
          errorMsg.classList.remove('hidden');
          container.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
        if (hasDiwali && dateSelect.value && dateSelect.value < minDateStr) {
          e.preventDefault();
          dateSelect.value = minDateStr;
          const errText = errorMsg.querySelector('.cr-delivery-slots__error-text');
          if (errText) {
            errText.textContent = 'Diwali gifts are only available for delivery post 20th October (starting 21st Oct).';
          }
          errorMsg.classList.remove('hidden');
          container.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
      });
      checkoutBtn.dataset.deliverySlotListenerBound = 'true';
    }

    // Always trigger updateTimeSlots on init to ensure time dropdown is enabled and prepopulated
    updateTimeSlots();
  }

  updateBlockerState(dateSelect, timeSelect, ctasContainer) {
    const isSelected = dateSelect.value && timeSelect.value;
    const buttonsWrap = document.querySelector('.additional-checkout-buttons');

    if (buttonsWrap) {
      if (!isSelected) {
        buttonsWrap.style.opacity = '0.35';
        buttonsWrap.style.pointerEvents = 'none';
        let blocker = document.querySelector('.cr-checkout-disabled-overlay__blocker');
        if (!blocker && ctasContainer) {
          blocker = document.createElement('div');
          blocker.className = 'cr-checkout-disabled-overlay__blocker';
          blocker.title = 'Please select a delivery date and time slot first';
          buttonsWrap.parentNode.insertBefore(blocker, buttonsWrap);
        }
      } else {
        buttonsWrap.style.opacity = '1';
        buttonsWrap.style.pointerEvents = 'auto';
        let blocker = document.querySelector('.cr-checkout-disabled-overlay__blocker');
        if (blocker) {
          blocker.remove();
        }
      }
    }
  }

  saveAttributesToCart(dateSelect, timeSelect, errorMsg, ctasContainer, isRequired) {
    const selectedDate = dateSelect.value;
    const selectedTime = timeSelect.value;

    if (isRequired) this.updateBlockerState(dateSelect, timeSelect, ctasContainer);

    fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          'Delivery Date': selectedDate,
          'Delivery Time Slot': selectedTime
        }
      })
    }).then(res => {
      if (res.ok) {
        if (selectedDate && selectedTime) {
          errorMsg.classList.add('hidden');
        }
      }
    });
  }
}

if (!customElements.get('cr-delivery-slots')) {
  customElements.define('cr-delivery-slots', CrDeliverySlots);
}
